import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Shield, AlertTriangle, AlertCircle, CheckCircle2, HelpCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { browsePair } from "../lib/api";
import { cn } from "../lib/utils";

const VERDICT_STYLES = {
  SAFE: { tone: "bg-emerald-50 text-emerald-800 border-emerald-200", Icon: CheckCircle2, headline: "No label conflict found" },
  CAUTION: { tone: "bg-amber-50 text-amber-900 border-amber-200", Icon: AlertTriangle, headline: "Use with caution" },
  AVOID: { tone: "bg-rose-50 text-rose-800 border-rose-200", Icon: AlertCircle, headline: "Avoid this combination" },
  INSUFFICIENT_DATA: { tone: "bg-cream-100 text-ink-soft border-surface-line", Icon: HelpCircle, headline: "Not yet reviewed", verdictLabel: "Not enough verified data" },
};

export default function BrowsePair() {
  const { medId, ingId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await browsePair(medId, ingId);
        setData(res);
      } catch (e) {
        setErr(e?.response?.status === 404 ? "Not found" : "Could not load");
      }
    })();
  }, [medId, ingId]);

  if (err) {
    return (
      <main className="max-w-screen-md mx-auto px-4 sm:px-6 md:px-10 py-16">
        <p className="text-ink-soft">{err}</p>
        <Link to="/browse" className="text-ink underline">← Back to browse</Link>
      </main>
    );
  }
  if (!data) return <main className="max-w-screen-md mx-auto px-4 sm:px-6 md:px-10 py-16 text-ink-mute">Loading…</main>;

  const { medication: m, ingredient: i, interaction, verdict, safety_score } = data;
  const style = VERDICT_STYLES[verdict] || VERDICT_STYLES.INSUFFICIENT_DATA;
  const Icon = style.Icon;

  return (
    <main data-testid="browse-pair-page" className="max-w-screen-md mx-auto px-4 sm:px-6 md:px-10 py-10 sm:py-14">
      <button
        data-testid="browse-back"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-[13px] text-ink-mute hover:text-ink mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <p className="text-[11px] uppercase tracking-[0.16em] text-ink-mute mb-2">
        Does it react?
      </p>
      <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-ink leading-tight">
        <Link to={`/browse/med/${m.id}`} className="hover:underline">
          {m.name || m.generic_name}
        </Link>
        <span className="text-ink-mute mx-2">×</span>
        <Link to={`/browse/ing/${i.id}`} className="hover:underline">
          {i.name}
        </Link>
      </h1>

      <div className={cn(
        "mt-6 rounded-2xl border p-5 flex items-start gap-4",
        style.tone,
      )}>
        <Icon className="w-7 h-7 shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="flex items-baseline gap-3 flex-wrap">
            <span data-testid="pair-verdict" className="text-xl font-bold uppercase tracking-wide">
              {style.verdictLabel || verdict}
            </span>
            {safety_score != null && (
              <span data-testid="pair-score" className="text-sm font-semibold opacity-80">
                Score {safety_score}/100
              </span>
            )}
          </div>
          <p className="mt-1 text-sm opacity-90">{style.headline}</p>
        </div>
      </div>

      {interaction ? (
        <section className="mt-8 space-y-5">
          {interaction.summary && (
            <div>
              <h2 className="text-[11px] uppercase tracking-[0.16em] text-ink-mute mb-2">Summary</h2>
              <p data-testid="pair-summary" className="text-base text-ink">{interaction.summary}</p>
            </div>
          )}
          {interaction.what_happens_plain && interaction.what_happens_plain !== interaction.summary && (
            <div>
              <h2 className="text-[11px] uppercase tracking-[0.16em] text-ink-mute mb-2">What happens</h2>
              <p className="text-base text-ink-soft">{interaction.what_happens_plain}</p>
            </div>
          )}
          {interaction.recommendation && (
            <div>
              <h2 className="text-[11px] uppercase tracking-[0.16em] text-ink-mute mb-2">What to do</h2>
              <p data-testid="pair-recommendation" className="text-base text-ink">{interaction.recommendation}</p>
            </div>
          )}
          {interaction.source_primary && interaction.source_primary_url && (
            <div className="pt-4 border-t border-surface-line text-[12.5px] text-ink-mute">
              Source:{" "}
              <a data-testid="pair-source-link" href={interaction.source_primary_url} target="_blank" rel="noopener noreferrer" className="underline font-medium text-ink">
                {interaction.source_primary} →
              </a>
            </div>
          )}
        </section>
      ) : (
        <p data-testid="pair-insufficient" className="mt-8 text-ink-soft">
          We don't have a verified, source-linked answer for this combination yet.
          Every verdict on BlendSafe links to its FDA or published source — we're
          adding them one row at a time. Always double-check with your prescriber
          if you have concerns.
        </p>
      )}

      <div className="mt-10 rounded-2xl border border-surface-line bg-cream-100 p-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-sm font-semibold text-ink">Check your own routine</p>
          <p className="text-[13px] text-ink-soft">Get a clear, source-linked verdict for all the products you use.</p>
        </div>
        <Button
          data-testid="pair-try-checker"
          onClick={() => navigate("/check")}
          className="bg-ink text-white hover:bg-ink/90"
        >
          Start free check
        </Button>
      </div>
    </main>
  );
}
